
import {
  computed, defineComponent, onMounted, reactive, ref,
} from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { CREATE_STEPS } from '@/types/enums';
import AppHeader from '@/components/AppHeader.vue';
import AddGroupName from '@/modules/spaces/components/AddGroup/AddGroupName.vue';
import AddGroupInfo from '@/modules/spaces/components/AddGroup/AddGroupInfo.vue';
import AddGroupSettings from '@/modules/spaces/components/AddGroup/AddGroupSettings.vue';
import AddGroupMembers from '@/modules/spaces/components/AddGroup/AddGroupMembers.vue';
import SpacesService from '@/modules/spaces/services/SpacesService';
import { ICreateGroup, IUpdateGroupData } from '@/types/interfaces';
import { useMainStore } from '@/store/mainStore';

export default defineComponent({
  components: {
    AppHeader,
    AddGroupName,
    AddGroupInfo,
    AddGroupSettings,
    AddGroupMembers,
  },
  setup() {
    const md = ref();
    const showDialog = ref(false);
    const currentStep = ref('STEP_NAME');
    const dataLoading = ref(false);
    const mainStore = useMainStore();
    const router = useRouter();
    const errorMessage = ref('');

    const registrationGroupData = reactive({
      name: '',
      ownerId: '',
      photo: null,
    } as ICreateGroup);

    const updateGroupData = ref({} as IUpdateGroupData);

    const usersToAdd = ref([] as string[]);

    const stepName = computed(() => {
      if (currentStep.value === CREATE_STEPS.NAME) {
        return 'Create group';
      }
      if (currentStep.value === CREATE_STEPS.INFO) {
        return 'Group info';
      }
      if (currentStep.value === CREATE_STEPS.SETTING) {
        return 'Group settings';
      }
      return 'Add Members';
    });

    function openDialog() {
      showDialog.value = true;
    }

    function close() {
      showDialog.value = false;
    }

    onClickOutside(md, close);

    function createGroup() {
      dataLoading.value = true;
      errorMessage.value = '';
      SpacesService.createGroup(registrationGroupData).then((res) => {
        if (res.success) {
          updateGroupData.value.name = res.results.data.name;
          updateGroupData.value.id = res.results.data.id;
          currentStep.value = CREATE_STEPS.INFO;
        } else {
          errorMessage.value = res.errorKey as string;
        }
      }).finally(() => { dataLoading.value = false; });
    }

    function updateGroupInfo(step: string) {
      dataLoading.value = true;
      const payload = { ...updateGroupData.value, photo: null };
      SpacesService.updateGroup(payload).then((res) => {
        if (res.success) {
          currentStep.value = step;
        }
      }).finally(() => { dataLoading.value = false; });
    }

    function addUsersToGroup() {
      dataLoading.value = true;
      SpacesService.addContactsToGroup(updateGroupData.value.id, usersToAdd.value).then((response) => {
        if (response) {
          dataLoading.value = false;
          router.push(`/spaces/groups/group/${updateGroupData.value.id}`);
          close();
        }
      });
    }

    function updateUsersToAdd(accountId: string) {
      if (usersToAdd.value.includes(accountId)) {
        usersToAdd.value.splice(usersToAdd.value.indexOf(accountId), 1);
      } else {
        usersToAdd.value.push(accountId);
      }
    }

    function setStep(data: { value: any; step: string }) {
      if (currentStep.value === CREATE_STEPS.NAME) {
        registrationGroupData.name = data.value;
        createGroup();
      } else {
        if (data.step === CREATE_STEPS.SETTING) {
          updateGroupData.value.groupInfo = data.value;
        }
        if (data.step === CREATE_STEPS.ADD) {
          updateGroupData.value.groupType = data.value;
        }
        updateGroupInfo(data.step);
      }
    }

    function stepBack() {
      switch (currentStep.value) {
        case CREATE_STEPS.INFO:
          currentStep.value = CREATE_STEPS.NAME;
          break;
        case CREATE_STEPS.SETTING:
          currentStep.value = CREATE_STEPS.INFO;
          break;
        case CREATE_STEPS.ADD:
          usersToAdd.value = [];
          currentStep.value = CREATE_STEPS.SETTING;
          break;
        default:
          break;
      }
    }

    function addLater() {
      switch (currentStep.value) {
        case CREATE_STEPS.INFO:
          currentStep.value = CREATE_STEPS.SETTING;
          break;
        case CREATE_STEPS.SETTING:
          currentStep.value = CREATE_STEPS.ADD;
          break;
        case CREATE_STEPS.ADD:
          router.push(`spaces/groups/group/${updateGroupData.value.id}`);
          break;
        default:
          break;
      }
    }

    onMounted(() => {
      registrationGroupData.ownerId = mainStore.user.accountId;
    });
    return {
      registrationGroupData,
      updateGroupData,
      dataLoading,
      CREATE_STEPS,
      md,
      showDialog,
      currentStep,
      usersToAdd,
      stepName,
      errorMessage,
      openDialog,
      close,
      setStep,
      stepBack,
      addLater,
      updateUsersToAdd,
      addUsersToGroup,
    };
  },
});
